/**
 * Pricing rules — docs/BUSINESS-RULES.md and ADR 0013.
 *
 * Customer price is cost divided by 0.6, a 40% gross profit margin. It is a
 * default, not a constraint: any line item price can be overridden afterwards.
 */

import { type Cents, cents, toCents } from "./money.js";

/** The company's target gross profit on catalogue pricing. */
export const GROSS_PROFIT_DIVISOR = 0.6;

/**
 * Default job overhead, as a percentage of the grand total.
 *
 * Always 10 (Jonathon, 2026-08-04). It was 17 in the prototype, and estimates
 * carried over from it still store 17 — including reference job #4014911, whose
 * golden test must keep computing at 17 because that is what the job was
 * actually priced at. This is the figure for anything new.
 */
export const DEFAULT_JOB_OVERHEAD_PCT = 10;

/**
 * Material orders are taxed at 10% by default; labour is not taxed.
 *
 * 10 from 2026-10-01 (Jonathon: "make the default material tax be 10% on jobs
 * going forward", ADR 0248). It was 9 before that, and orders written under
 * 9 keep it — this is only what a new order starts at. The column defaults on
 * material_orders and material_returns say the same number.
 */
export const DEFAULT_MATERIAL_TAX_RATE = 10;
export const DEFAULT_WORK_TAX_RATE = 0;

/**
 * Customer price from cost: `cost / 0.6`, rounded to the cent.
 *
 * Example from BUSINESS-RULES.md: cost 37.33 -> price 62.22.
 */
export function priceFromCost(cost: unknown): Cents {
  const c = toCents(cost);
  return priceFromCents(c);
}

/** Customer price when the cost is already at the domain's cents boundary. */
export function priceFromCents(cost: Cents): Cents {
  if (cost === 0) return 0 as Cents;
  return cents(cost / GROSS_PROFIT_DIVISOR);
}

/**
 * Auto-quantity from a roof measurement — ADR 0021, currently dormant.
 *
 * `qty = ceil(measurement x (1 + waste/100) / coverage)`
 *
 * The 180 products carrying Roofr's measurement/coverage/waste data feed this.
 * The formula still needs the owner's confirmation (open item F2) before it is
 * wired into the estimate builder, but it is pure arithmetic and testable now.
 */
export function autoQuantity(input: {
  measurement: unknown;
  coverage: unknown;
  waste?: unknown;
}): number | null {
  const measurement = Number(input.measurement);
  const coverage = Number(input.coverage);
  const waste = Number(input.waste ?? 0);
  if (!Number.isFinite(measurement) || measurement <= 0) return null;
  if (!Number.isFinite(coverage) || coverage <= 0) return null;
  const wastePct = Number.isFinite(waste) ? waste : 0;
  return Math.ceil((measurement * (1 + wastePct / 100)) / coverage);
}
